import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { useKeytipRef } from "@fluentui/react";
import { useMergedRefs } from "@fluentui/react-hooks";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IKeytipProps } from "@fluentui/react";
import type { RefObjectFunction } from "@fluentui/react-hooks";

import type { OnExecuteParameter, UILocation } from "@1js/acui-common";

import { defaultKeytipExecute } from "./keytipExecution";
import { useOnExecuteFromKeytip } from "./useOnExecuteFromKeytip";

export type AppComponentKeytipProps = {
  checked?: boolean;
  id: string;
  location?: UILocation;
  onExecute?: <TOnExecuteParameter extends OnExecuteParameter>(
    parameter: TOnExecuteParameter
  ) => void;
  shouldTakeFocus?: boolean;
  toggled?: boolean;
};

export type AppComponentKeytipOptions = {
  directClickRef?: React.RefObject<HTMLElement | null | undefined>;
  keytipProps?: IKeytipProps;
  useDirectClick?: boolean;
};

export type AppComponentWithKeytip =
  | "AppButton"
  | "AppCheckbox"
  | "AppCombobox"
  | "AppInput"
  | "AppMenuItem"
  | "AppPromptInput"
  | "AppSuggestion"
  | "AppTextarea";

export type DeriveKeytipFunction = (
  keytipProps?: IKeytipProps,
  onExecute?: (executeTarget: HTMLElement, target?: HTMLElement) => void
) => IKeytipProps | undefined;

export const useAppComponentKeytip: <TProps extends AppComponentKeytipProps>(
  props: TProps,
  ref: RefObjectFunction<HTMLElement | null | undefined>,
  componentType: AppComponentWithKeytip,
  deriveKeytip: DeriveKeytipFunction,
  options?: AppComponentKeytipOptions
) => RefObjectFunction<HTMLElement | null | undefined> = (
  props,
  ref,
  type,
  deriveKeytip,
  options
) => {
  const { keytipProps } = options || {};

  const executeFromKeytip = useOnExecuteFromKeytip(props, ref, type, options);

  const keytipExecute = React.useCallback(
    (executeTarget: HTMLElement) => {
      defaultKeytipExecute(executeTarget, executeFromKeytip);
    },
    [executeFromKeytip]
  );
  const keytipRefData = React.useMemo(() => {
    return { keytipProps: deriveKeytip(keytipProps, keytipExecute) };
  }, [deriveKeytip, keytipProps, keytipExecute]);

  const localRef = useKeytipRef(keytipRefData);
  const mergedRef = useMergedRefs<HTMLElement | null | undefined>(
    ref,
    localRef
  );

  return mergedRef;
};
